import { useState, useCallback, useEffect, useRef } from 'react';
import { BranchService } from '../services/branch-service';
import { BranchFiles } from '../types';
import { useToast } from '../context/toast-context';

interface UseBranchFilesReturn {
  files: BranchFiles['files'] | null;
  isLoading: boolean;
  error: string | null;
  fetchFiles: () => Promise<void>;
  clearFiles: () => void;
}

export const useBranchFiles = (
  branchId: number | null,
  enabled: boolean = true
): UseBranchFilesReturn => {
  const [files, setFiles] = useState<BranchFiles['files'] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();
  
  // Ref para evitar llamadas duplicadas
  const hasFetched = useRef<Set<number>>(new Set());
  const branchServiceRef = useRef<BranchService | null>(null);

  // Inicializar el servicio una sola vez
  if (!branchServiceRef.current) {
    branchServiceRef.current = new BranchService();
  }

  const fetchFiles = useCallback(async () => {
    if (!branchId || !enabled || !branchServiceRef.current) return;

    // Evitar llamadas duplicadas para el mismo branchId
    if (hasFetched.current.has(branchId)) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await branchServiceRef.current.getBranchFiles(
        branchId,
        (data) => {
          setFiles(data.files);
          hasFetched.current.add(branchId); // Marcar como ya obtenido
        },
        (errorMessage) => {
          setError(errorMessage);
          showToast(`Error al cargar archivos: ${errorMessage}`, 'error');
        }
      );
    } catch (err) {
      const errorMsg = 'Error inesperado al cargar archivos';
      setError(errorMsg);
      showToast(errorMsg, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [branchId, enabled, showToast]);

  const clearFiles = useCallback(() => {
    setFiles(null);
    setError(null);
    // Limpiar el cache cuando se limpian los archivos
    if (branchId) {
      hasFetched.current.delete(branchId);
    }
  }, [branchId]);

  // Auto-fetch solo una vez cuando cambie branchId y esté habilitado
  useEffect(() => {
    if (branchId && enabled && !hasFetched.current.has(branchId)) {
      fetchFiles();
    } else if (!branchId || !enabled) {
      clearFiles();
    }
  }, [branchId, enabled, fetchFiles, clearFiles]);

  return {
    files,
    isLoading,
    error,
    fetchFiles,
    clearFiles,
  };
};
